import { createProtanPolygonVisualizations } from './ProtanPolygonVisualizations';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import {
  AbstractSheetFilledShape,
  SheetFillStyleSet,
} from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetFilledShape';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import {
  MultiPolygon2Fragment,
  SheetShapeDeepFragment,
  SheetScaleType,
  GetSparkelPropertiesForProjectQuery,
} from 'src/gql/graphql';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import { ChakraColor } from 'src/utils/color-util';

type PolygonShapeType = {
  exterior: { points: THREE.Vector3[] };
  interiors: { points: THREE.Vector3[] }[];
};

export class PolygonShape extends AbstractSheetFilledShape {
  private protanShapes: {
    fireSectioningShapes: Autodesk.Edit2D.Shape[];
    cornerAreaShapes: Autodesk.Edit2D.Shape[];
    randAreaShapes: Autodesk.Edit2D.Shape[];
  } = {
    fireSectioningShapes: [],
    cornerAreaShapes: [],
    randAreaShapes: [],
  };

  // eslint-disable-next-line complexity
  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    polygons?: PolygonShapeType[],
    styleSet?: SheetFillStyleSet,
    isProtan?: boolean,
    externalShapes?: SheetShapeDeepFragment[],
    calibration?: SheetScaleType,
    sparkelProperties?: GetSparkelPropertiesForProjectQuery
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);
    const convertPoints = !!(shapeModel && shapeModel.sheetShapePolygon);
    const mappedPolygons =
      shapeModel && shapeModel.sheetShapePolygon
        ? shapeModel.sheetShapePolygon.multipolygon.polygons
        : polygons;

    if (shapeModel && !shapeModel.sheetShapePolygon) {
      throw new Error('shapeModel.sheetShapePolygon must be defined');
    } else if (!shapeModel && !Array.isArray(polygons)) {
      throw new Error('One shapeModel or polygons must be passed');
    } else if (!mappedPolygons || !Array.isArray(mappedPolygons)) {
      throw new Error('Cannot define polygons');
    }

    // Create the main polygon shapes
    this.shapes = mappedPolygons.map((polygon) => {
      const svg = this._toSVG(polygon, convertPoints);

      // https://aps.autodesk.com/en/docs/viewer/v7/developers_guide/advanced_options/edit2d-manual/#import-and-convert-svg-shapes
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return Autodesk.Edit2D.Shape.fromSVG(svg);
    });

    // Create Protan visualizations for Protan tenants
    if (isProtan && shapeModel && externalShapes && calibration) {
      this.createProtanVisualizations(
        shapeModel,
        externalShapes,
        calibration,
        sparkelProperties
      );
    }

    this.textLabelPoints = this.shapes.map((shape) => {
      // Ignored because Box2.center() is deprecated and there is no typed Box2.getCenter()
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return shape.getBBox().getCenter();
    });

    // For Protan tenants, make the main shape transparent to see overlaid visualizations
    if (isProtan && styleSet) {
      const protanStyleSet = { ...styleSet };
      if (protanStyleSet.default) {
        const usesFireSectioning =
          this.protanShapes.fireSectioningShapes.length > 0;
        protanStyleSet.default = {
          ...protanStyleSet.default,
          fillAlpha: usesFireSectioning ? 0 : 0.5, // Make main shape transparent
          fillColor: usesFireSectioning
            ? protanStyleSet.default.fillColor
            : ChakraColor.yellow300,
        };
        protanStyleSet.hovered = {
          ...protanStyleSet.hovered,
          fillAlpha: usesFireSectioning ? 0.07 : 0.5, // Slight transparency on hover
        };
      }
      this.styleSet = protanStyleSet;
    } else {
      this.styleSet = styleSet || null;
    }

    this.setStyle(SheetShapeStyleType.default);
  }

  private createProtanVisualizations(
    shapeModel: SheetShapeDeepFragment,
    externalShapes: SheetShapeDeepFragment[],
    calibration: SheetScaleType,
    sparkelProperties?: GetSparkelPropertiesForProjectQuery
  ) {
    const protanVisualizations = createProtanPolygonVisualizations(
      shapeModel,
      externalShapes,
      calibration,
      sparkelProperties,
      (point) => super.convertPointToRender(point)
    );

    this.protanShapes.fireSectioningShapes =
      protanVisualizations.fireSectioningShapes;
    this.protanShapes.cornerAreaShapes = protanVisualizations.cornerAreaShapes;
    this.protanShapes.randAreaShapes = protanVisualizations.randAreaShapes;
  }

  private _toSVG(
    polygon: MultiPolygon2Fragment['polygons'][number] | PolygonShapeType,
    convertPoints = true
  ): string {
    const exterior =
      'M ' +
      polygon.exterior.points
        .map((point) => {
          const converted = convertPoints
            ? super.convertPointToRender(point)
            : point;

          return `${converted.x},${converted.y}`;
        })
        .join(' L ');
    const interiors = polygon.interiors
      .map(
        ({ points }) =>
          'M ' +
          points
            .map((point) => {
              const converted = convertPoints
                ? super.convertPointToRender(point)
                : point;

              return `${converted.x},${converted.y}`;
            })
            .join(' L ')
      )
      .join(' ');

    return interiors?.length
      ? `<path d="${exterior} Z ${interiors} Z"/>`
      : `<path d="${exterior} Z"/>`;
  }

  draw() {
    super.draw();

    // Respect wind load zones toggle from localStorage (no global state)
    let showWindLoadZones = true;
    try {
      const stored = window?.localStorage?.getItem('show-wind-load-zones');
      showWindLoadZones =
        stored && stored !== 'undefined' ? JSON.parse(stored) : true;
    } catch {
      // default to true on parse/access errors
      showWindLoadZones = true;
    }

    // Explicitly add Protan visualization shapes to the Edit2D layer
    if (showWindLoadZones) {
      this.protanShapes.randAreaShapes.forEach((shape) => {
        this.edit2d.defaultContext.layer.addShape(shape);
      });
      this.protanShapes.cornerAreaShapes.forEach((shape) => {
        this.edit2d.defaultContext.layer.addShape(shape);
      });
    }
    this.protanShapes.fireSectioningShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.addShape(shape);
    });
  }

  /**
   * Listen for runtime toggle events to add/remove wind load zones without full redraw
   */
  public attachWindLoadZonesListener() {
    const handler = (e: Event) => {
      const enabled = (e as CustomEvent).detail?.enabled;
      if (enabled === undefined) return;
      if (enabled) {
        this.protanShapes.randAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.addShape(shape);
        });
        this.protanShapes.cornerAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.addShape(shape);
        });
        // Ensure fire sectioning shapes are always on top by re-adding them last
        this.protanShapes.fireSectioningShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.removeShape(shape);
        });
        this.protanShapes.fireSectioningShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.addShape(shape);
        });
      } else {
        this.protanShapes.randAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.removeShape(shape);
        });
        this.protanShapes.cornerAreaShapes.forEach((shape) => {
          this.edit2d.defaultContext.layer.removeShape(shape);
        });
      }
      this.viewer.impl.invalidate(true, true, true);
    };

    window.addEventListener('SheetsV2:windLoadZonesToggle', handler);

    // Store a remover on the instance so we can clean up later
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._removeWindToggleHandler = () =>
      window.removeEventListener('SheetsV2:windLoadZonesToggle', handler);
  }

  remove() {
    super.remove();

    // Explicitly remove all Protan visualization shapes from the Edit2D layer
    this.protanShapes.randAreaShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
    this.protanShapes.cornerAreaShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
    this.protanShapes.fireSectioningShapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
  }
}
